import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Image as ImageIcon, Download, Copy, X } from "lucide-react";

export default function History() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ads");
      setAds(data);
    } catch (e) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Excluir este anúncio?")) return;
    try {
      await api.delete(`/ads/${id}`);
      setAds((prev) => prev.filter((a) => a.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Anúncio excluído");
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const copyText = (text, label) => {
    try {
      navigator.clipboard.writeText(text || "");
      toast.success(`${label} copiado!`);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const downloadImage = (ad) => {
    const src = ad.mode === "video" && ad.video_url
      ? `${process.env.REACT_APP_BACKEND_URL}${ad.video_url}`
      : ad.image;
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = `avsl-anuncio-${ad.id}.${ad.mode === "video" ? "mp4" : "png"}`;
    a.click();
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      <div className="mb-10">
        <div className="overline mb-3">Histórico</div>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.9]">
          Seus <span style={{ color: "var(--avsl-red)" }}>anúncios</span>
        </h1>
        <p className="mt-4 text-neutral-400">Todos os anúncios que você já gerou. Copie, baixe ou exclua.</p>
      </div>

      {loading ? (
        <div className="text-neutral-500 font-mono-tech" data-testid="history-loading">Carregando...</div>
      ) : ads.length === 0 ? (
        <div className="avsl-card p-10 text-center" data-testid="history-empty">
          <div className="font-display text-3xl">Nenhum anúncio ainda</div>
          <p className="text-neutral-500 mt-3">Vá para o gerador e crie seu primeiro anúncio.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="history-grid">
          {ads.map((ad) => (
            <button
              key={ad.id}
              onClick={() => setSelected(ad)}
              className="avsl-card text-left group relative overflow-hidden"
              data-testid={`ad-card-${ad.id}`}
            >
              <div className="aspect-square bg-black flex items-center justify-center overflow-hidden relative">
                {ad.mode === "video" && ad.video_url ? (
                  <>
                    <video
                      src={`${process.env.REACT_APP_BACKEND_URL}${ad.video_url}`}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 text-[10px] font-mono-tech uppercase tracking-widest" style={{ background: "var(--avsl-red)", color: "#fff" }}>
                      Vídeo
                    </div>
                  </>
                ) : ad.image ? (
                  <img src={ad.image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-neutral-600">
                    <ImageIcon size={40} strokeWidth={1} />
                    <span className="text-[10px] uppercase tracking-widest font-mono-tech">
                      {ad.status === "pending" || ad.status === "generating_media" ? "Gerando..." : "Sem mídia"}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="font-display text-xl leading-tight line-clamp-2">
                  {ad.copy?.headline || "Sem título"}
                </div>
                <div className="text-xs text-neutral-500 mt-2 font-mono-tech">
                  {new Date(ad.created_at).toLocaleString("pt-BR")}
                </div>
              </div>
              <button
                onClick={(e) => del(ad.id, e)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center border opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "rgba(0,0,0,0.7)", borderColor: "var(--avsl-red)" }}
                data-testid={`delete-${ad.id}`}
              >
                <Trash2 size={14} strokeWidth={2} color="var(--avsl-red-soft)" />
              </button>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={() => setSelected(null)}
          data-testid="ad-modal"
        >
          <div
            className="avsl-card max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0" style={{ background: "var(--avsl-surface)", borderColor: "var(--avsl-border)" }}>
              <div className="font-display text-2xl">{selected.copy?.headline}</div>
              <button onClick={() => setSelected(null)} data-testid="close-modal" className="text-neutral-400 hover:text-white">
                <X size={22} strokeWidth={2} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              {selected.mode === "video" && selected.video_url ? (
                <div>
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}${selected.video_url}`}
                    controls autoPlay loop
                    className="w-full object-contain bg-black max-h-[60vh]"
                  />
                  <button onClick={() => downloadImage(selected)} className="avsl-btn-ghost avsl-btn w-full mt-3">
                    <Download size={16} strokeWidth={2.5} className="mr-2" /> Baixar vídeo
                  </button>
                </div>
              ) : selected.image ? (
                <div>
                  <img src={selected.image} alt="" className="w-full object-contain bg-black" />
                  <button onClick={() => downloadImage(selected)} className="avsl-btn-ghost avsl-btn w-full mt-3">
                    <Download size={16} strokeWidth={2.5} className="mr-2" /> Baixar imagem
                  </button>
                </div>
              ) : null}
              <div className="space-y-4">
                <Field label="Sub-headline" value={selected.copy?.subheadline} onCopy={copyText} />
                <Field label="Descrição" value={selected.copy?.description} onCopy={copyText} />
                <Field label="Legenda" value={selected.copy?.caption} onCopy={copyText} multiline />
                <div>
                  <div className="overline mb-2">Hashtags</div>
                  <div className="flex flex-wrap gap-2">
                    {(selected.copy?.hashtags || []).map((h, i) => (
                      <span key={i} className="px-2 py-1 text-xs border font-mono-tech" style={{ borderColor: "var(--avsl-red)", color: "var(--avsl-red-soft)" }}>{h}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="overline mb-2">CTA</div>
                  <div className="font-display text-2xl" style={{ color: "var(--avsl-red)" }}>{selected.copy?.cta}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onCopy, multiline }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="overline">{label}</span>
        <button onClick={() => onCopy(value, label)} className="text-xs text-neutral-400 hover:text-white">
          <Copy size={12} strokeWidth={2} className="inline mr-1" /> Copiar
        </button>
      </div>
      <div className={`border-l-2 pl-3 text-neutral-200 ${multiline ? "whitespace-pre-wrap text-sm" : ""}`} style={{ borderColor: "var(--avsl-red)" }}>
        {value || <span className="text-neutral-500">—</span>}
      </div>
    </div>
  );
}
